import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "~/components/ui/chart";
import type { ChartConfig } from "~/components/ui/chart";
import { useV1UtilizationData } from "~/hooks/useV1UtilizationData";
import { useV2HistoricalData } from "~/hooks/useV2Data";

interface ProtocolUtilizationChartProps {
  days?: number;
}

const chartConfig = {
  v1: {
    label: "Cooler V1",
    color: "hsl(var(--chart-1))",
  },
  v2: {
    label: "Cooler V2",
    color: "hsl(var(--chart-2))",
  },
} satisfies ChartConfig;

const formatUSD = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};



export function ProtocolUtilizationChart({ days = 90 }: ProtocolUtilizationChartProps) {
  // Get v1 data using shared hook
  const { data: v1Data, isLoading: v1Loading, hasError: v1HasError, isEmpty: v1IsEmpty } = useV1UtilizationData();
  
  // Get v2 data
  const { data: v2Data, isLoading: v2Loading } = useV2HistoricalData(days);

  const isLoading = v1Loading || v2Loading;

  if (isLoading) return null;
  if (v1HasError) return null;
  if (v1IsEmpty) return null;

  // Process V1 data - extract just the totalPrincipalReceivables by date
  const v1ValuesByDate = new Map<string, number>();
  v1Data.forEach((dataPoint) => {
    v1ValuesByDate.set(dataPoint.date, dataPoint.totalPrincipalReceivables);
  });

  // Process V2 data - collect all dates and organize values by date
  const v2ValuesByDate = new Map<string, number>();
  if (v2Data) {
    v2Data.forEach((dataPoint) => {
      const date = new Date(dataPoint.timestamp / 1000).toISOString().split("T")[0];
      v2ValuesByDate.set(date, dataPoint.totalDebt);
    });
  }

  // Combine data - get all unique dates
  const allDates = new Set([...v1ValuesByDate.keys(), ...v2ValuesByDate.keys()]);
  const sortedDates = Array.from(allDates).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );

  // Keep track of last known values for forward-filling
  let lastV1Value = 0;
  let lastV2Value = 0;

  // Process each date chronologically and forward-fill values
  const chartData = sortedDates.map((date) => {
    // Update last known values if we have new data for this date
    if (v1ValuesByDate.has(date)) {
      lastV1Value = v1ValuesByDate.get(date)!;
    }
    if (v2ValuesByDate.has(date)) {
      lastV2Value = v2ValuesByDate.get(date)!;
    }
    
    return {
      date,
      v1: lastV1Value,
      v2: lastV2Value,
      total: lastV1Value + lastV2Value,
    };
  }).filter(item => item.date); // Remove items with invalid dates

  // Calculate current totals from the latest data point
  const currentV1 = lastV1Value;
  const currentV2 = lastV2Value;
  const currentTotal = currentV1 + currentV2;

  // Return null if no data
  if (chartData.length === 0) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Protocol Utilization</CardTitle>
        <div className="text-sm text-muted-foreground">
          Total Borrowed: {formatUSD(currentTotal)}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ChartContainer config={chartConfig}>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart
              data={chartData}
              margin={{ top: 0, right: 10, bottom: 0, left: 12 }}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => value.split("-").slice(1).join("/")}
                tickLine={false}
                axisLine={false}
              />
              <YAxis tickFormatter={formatUSD} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              {Object.entries(chartConfig).map(([key, config]) => (
                <Line
                  key={key}
                  type="monotone"
                  name={config.label}
                  dataKey={key}
                  stroke={`var(--color-${key})`}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
} 